import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { MapPin, UserPlus, Eye, EyeOff } from 'lucide-react';
import { FaGoogle } from 'react-icons/fa';
import { motion } from 'framer-motion';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, loginWithGoogle, error } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    surname: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    
    if (formData.password !== formData.confirmPassword) {
      setLocalError('Le password non corrispondono');
      return;
    }
    
    if (formData.password.length < 6) {
      setLocalError('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    setIsLoading(true);
    
    const result = await register(
      formData.email,
      formData.password,
      formData.name,
      formData.surname
    );
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setLocalError(result.error);
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="border-[#E4E4E7] shadow-sm">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-[#0055FF] rounded-xl flex items-center justify-center">
                <MapPin className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-[#09090B]">Crea Account</CardTitle>
            <CardDescription className="text-[#71717A]">Registrati per iniziare</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(localError || error) && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {localError || error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-semibold text-[#09090B]">Nome</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Mario"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="register-name-input"
                    className="border-[#E4E4E7] focus:ring-[#0055FF] focus:border-[#0055FF]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surname" className="text-sm font-semibold text-[#09090B]">Cognome</Label>
                  <Input
                    id="surname"
                    name="surname"
                    type="text"
                    placeholder="Rossi"
                    value={formData.surname}
                    onChange={handleChange}
                    required
                    data-testid="register-surname-input"
                    className="border-[#E4E4E7] focus:ring-[#0055FF] focus:border-[#0055FF]"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold text-[#09090B]">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="nome@esempio.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="register-email-input"
                  className="border-[#E4E4E7] focus:ring-[#0055FF] focus:border-[#0055FF]"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold text-[#09090B]">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    data-testid="register-password-input"
                    className="border-[#E4E4E7] focus:ring-[#0055FF] focus:border-[#0055FF] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#09090B]"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-semibold text-[#09090B]">Conferma Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  data-testid="register-confirm-password-input"
                  className="border-[#E4E4E7] focus:ring-[#0055FF] focus:border-[#0055FF]"
                />
              </div>
              
              <Button
                type="submit"
                disabled={isLoading}
                data-testid="register-submit-button"
                className="w-full bg-[#0055FF] hover:bg-[#0040D0] text-white font-semibold py-3"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Registrazione in corso...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Registrati
                  </span>
                )}
              </Button>
            </form>
            
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E4E4E7]" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-[#71717A]">oppure</span>
              </div>
            </div>
            
            <Button
              type="button"
              variant="outline"
              onClick={loginWithGoogle}
              data-testid="google-register-button"
              className="w-full border-[#E4E4E7] hover:bg-[#F4F4F5] text-[#09090B] font-semibold py-3"
            >
              <FaGoogle className="w-4 h-4 mr-2 text-[#DB4437]" />
              Continua con Google
            </Button>
            
            <p className="text-center text-sm text-[#71717A] mt-6">
              Hai già un account?{' '}
              <Link 
                to="/login" 
                className="text-[#0055FF] hover:underline font-semibold"
                data-testid="login-link"
              >
                Accedi
              </Link>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
